import * as React from 'react';
import {
  Chart, BarController, BarElement, CategoryScale, LinearScale, Tooltip, Legend,
} from 'chart.js';
import { ICapacite } from '../../../models';
import { getContentColor } from '../../shared/ProductColors';
import styles from './CapacityChart.module.scss';

Chart.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

export interface ICapacityChartProps {
  capacites: ICapacite[];
}

export const CapacityChart: React.FC<ICapacityChartProps> = ({ capacites }) => {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const chartRef = React.useRef<Chart | null>(null);

  React.useEffect(() => {
    if (!canvasRef.current) return;

    // Aggregate by depot × product
    const depotOrder: string[] = [];
    const depotProducts: Record<string, Record<string, number>> = {};
    const allProducts = new Set<string>();

    capacites.forEach(c => {
      if (!depotProducts[c.Depot]) {
        depotProducts[c.Depot] = {};
        depotOrder.push(c.Depot);
      }
      depotProducts[c.Depot][c.Produit] = (depotProducts[c.Depot][c.Produit] || 0) + c.VolumeAlloue;
      allProducts.add(c.Produit);
    });

    const productList = Array.from(allProducts);

    const datasets = productList.map(produit => {
      const color = getContentColor(produit);
      return {
        label: produit,
        data: depotOrder.map(depot => depotProducts[depot][produit] || 0),
        backgroundColor: color.background,
        borderColor: color.background,
        borderWidth: 1,
        borderRadius: 3,
      };
    });

    if (chartRef.current) {
      chartRef.current.destroy();
    }

    chartRef.current = new Chart(canvasRef.current, {
      type: 'bar',
      data: {
        labels: depotOrder,
        datasets,
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              usePointStyle: true,
              pointStyle: 'circle',
              padding: 14,
              font: { family: "'DM Sans', sans-serif", size: 11 },
            },
          },
          tooltip: {
            callbacks: {
              label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y.toLocaleString('fr-FR')} m³`,
            },
          },
        },
        scales: {
          x: {
            stacked: true,
            grid: { display: false },
            ticks: {
              font: { family: "'DM Sans', sans-serif", size: 10 },
              maxRotation: 45,
            },
          },
          y: {
            stacked: true,
            grid: { color: 'rgba(0,0,0,0.05)' },
            ticks: {
              font: { family: "'DM Sans', sans-serif", size: 10 },
              callback: (val) => `${(val as number / 1000).toFixed(0)}k`,
            },
          },
        },
      },
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [capacites]);

  return (
    <div className={styles.chartWrapper}>
      <div className={styles.chartTitle}>Capacité par dépôt</div>
      <div className={styles.chartContainer}>
        <canvas ref={canvasRef} />
      </div>
    </div>
  );
};
